import type { Dispatch, SetStateAction } from 'react'
import dayjs from 'dayjs'
import { Button, Input, Space, Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { ID } from '@/types/common'
import { Toast } from '@/components/Toast'
import classRecordApi from './api'
import {
  CLASS_RECORD_KIND_LABEL,
  CLASS_RECORD_STATUS_LABEL,
  CLASS_TYPE_LABEL,
  WEEKDAY_LABEL,
} from './constants'
import { getDailySequenceColor, getRecordConfigText, idKey } from './helpers'
import {
  ClassRecord,
  ClassRecordKind,
  ClassRecordStatus,
  StudentClassConfigVersion,
  StudentClassConfigVersionGenerationBatch,
} from './types'

function getWeekdaySummary(weekdays: number[]) {
  if (!weekdays.length) {
    return '未设置上课日'
  }

  return weekdays.map((day) => WEEKDAY_LABEL[day] || day).join(' / ')
}

function getConfigSummary(record: StudentClassConfigVersion) {
  const pricingSummary = record.isAllDayPricing
    ? `全天班计费${record.monthlyPrice !== undefined && record.monthlyPrice !== null ? ` · ¥${record.monthlyPrice}` : ''}`
    : '模板计费'

  return `${getWeekdaySummary(record.weekdays)} · ${pricingSummary} · ${record.templates.length} 个模板`
}

function getLatestGenerationBatch(
  generationBatches: StudentClassConfigVersionGenerationBatch[],
) {
  if (!generationBatches.length) {
    return null
  }

  return generationBatches.reduce((latest, batch) =>
    dayjs(batch.createdAt).isAfter(dayjs(latest.createdAt)) ? batch : latest,
  )
}

type CreateVersionBatchColumnsOptions = {
  deletingBatchId: ID | null
  onDelete: (batchId: ID) => void
}

export function createVersionBatchColumns({
  deletingBatchId,
  onDelete,
}: CreateVersionBatchColumnsOptions): ColumnsType<StudentClassConfigVersionGenerationBatch> {
  return [
    {
      title: '生成范围',
      key: 'generatedRange',
      width: 240,
      render: (_: unknown, record) =>
        `${record.generatedStartDate} ~ ${record.generatedEndDate}`,
    },
    {
      title: '生成时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (value: string) => dayjs(value).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      render: (_: unknown, record) => (
        <Space size={0}>
          <Button
            type="link"
            danger
            loading={deletingBatchId === record.id}
            onClick={() => onDelete(record.id)}
          >
            回滚
          </Button>
        </Space>
      ),
    },
  ]
}

type CreateVersionColumnsOptions = {
  deletingVersionId: ID | null
  selectedStudentId: ID | undefined
  onDelete: (record: StudentClassConfigVersion) => void
  onReloadCurrentConfig: (studentId: ID) => Promise<void>
  onViewGenerationBatches: (record: StudentClassConfigVersion) => void
}

export function createVersionColumns({
  deletingVersionId,
  selectedStudentId,
  onDelete,
  onReloadCurrentConfig,
  onViewGenerationBatches,
}: CreateVersionColumnsOptions): ColumnsType<StudentClassConfigVersion> {
  return [
    {
      title: (
        <span title="按配置版本创建时间排序和展示，不再单独维护生效日期">
          创建时间
        </span>
      ),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 124,
      render: (value: string) => dayjs(value).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: '当前',
      dataIndex: 'isCurrent',
      key: 'isCurrent',
      width: 88,
      render: (value: boolean) => (value ? <Tag color="green">当前</Tag> : <Tag>历史</Tag>),
    },
    {
      title: '配置摘要',
      key: 'configSummary',
      render: (_: unknown, record) => (
        <div className="min-w-0 leading-5">
          <div className="whitespace-normal break-words text-[13px] text-slate-900">
            {getConfigSummary(record)}
          </div>
        </div>
      ),
    },
    {
      title: '批次',
      key: 'generationBatchSummary',
      width: 156,
      render: (_: unknown, record) => {
        const latestBatch = getLatestGenerationBatch(record.generationBatches)

        return (
          <div className="leading-5">
            <div className="text-sm font-medium text-slate-900">
              {record.generationBatchCount} 个批次
            </div>
            <div className="text-xs text-slate-500">
              {latestBatch
                ? `最近 ${dayjs(latestBatch.createdAt).format('MM-DD HH:mm')}`
                : '尚未生成批次'}
            </div>
          </div>
        )
      },
    },
    {
      title: '操作',
      key: 'actions',
      width: 220,
      render: (_: unknown, record) => (
        <Space size={0}>
          <Button
            type="link"
            onClick={() => onViewGenerationBatches(record)}
          >
            查看详情
          </Button>
          <Button
            type="link"
            disabled={record.isCurrent || !selectedStudentId}
            onClick={async () => {
              if (!selectedStudentId) return
              try {
                await classRecordApi.setCurrentVersion(selectedStudentId, record.id)
                Toast.success('已设为当前配置')
                await onReloadCurrentConfig(selectedStudentId)
              } catch {
                Toast.error('设置失败')
              }
            }}
          >
            设为当前
          </Button>
          <Button
            type="link"
            danger
            disabled={!record.canDelete}
            loading={deletingVersionId === record.id}
            onClick={() => onDelete(record)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ]
}

type CreateRecordColumnsOptions = {
  currentConfigMonthlyPrice?: number | null
  dailySequenceMap: Record<string, number>
  dateGroupMeta: {
    rowSpanMap: Record<string, number>
  }
  generationBatchMonthlyPriceMap: Record<string, number | null>
  noteDraftMap: Record<string, string>
  noteSavingMap: Record<string, boolean>
  setNoteDraftMap: Dispatch<SetStateAction<Record<string, string>>>
  unifiedMonthlyPriceByDate: Record<string, number | null>
  onNoteBlur: (record: ClassRecord) => void
}

export function createRecordColumns({
  currentConfigMonthlyPrice,
  dailySequenceMap,
  dateGroupMeta,
  generationBatchMonthlyPriceMap,
  noteDraftMap,
  noteSavingMap,
  setNoteDraftMap,
  unifiedMonthlyPriceByDate,
  onNoteBlur,
}: CreateRecordColumnsOptions): ColumnsType<ClassRecord> {
  return [
    {
      title: '日期',
      dataIndex: 'classDate',
      key: 'classDate',
      width: 140,
      render: (_: string, record: ClassRecord) => {
        const key = idKey(record.id)
        const rowSpan = dateGroupMeta.rowSpanMap[key] ?? 1
        if (rowSpan === 0) {
          return {
            children: null,
            props: {
              rowSpan: 0,
            },
          }
        }

        return {
          children: dayjs(record.classDate).format('YYYY-MM-DD'),
          props: {
            rowSpan,
          },
        }
      },
    },
    {
      title: '周几',
      dataIndex: 'weekday',
      key: 'weekday',
      width: 96,
      render: (_: number, record: ClassRecord) => {
        const key = idKey(record.id)
        const rowSpan = dateGroupMeta.rowSpanMap[key] ?? 1
        if (rowSpan === 0) {
          return {
            children: null,
            props: {
              rowSpan: 0,
            },
          }
        }

        const weekday =
          record.weekday ||
          (dayjs(record.classDate).day() === 0 ? 7 : dayjs(record.classDate).day())

        return {
          children: (
            <Tag className="m-0 rounded-full px-2">
              {WEEKDAY_LABEL[weekday] || '-'}
            </Tag>
          ),
          props: {
            rowSpan,
          },
        }
      },
    },
    {
      title: '记录类型',
      dataIndex: 'recordKind',
      key: 'recordKind',
      width: 110,
      render: (value: ClassRecordKind) => {
        const color =
          value === ClassRecordKind.MAKEUP_USAGE
            ? 'orange'
            : value === ClassRecordKind.MANUAL
              ? 'blue'
              : 'default'
        return <Tag color={color}>{CLASS_RECORD_KIND_LABEL[value]}</Tag>
      },
    },
    {
      title: '状态',
      dataIndex: 'classRecordStatus',
      key: 'classRecordStatus',
      width: 112,
      render: (value: ClassRecordStatus, record: ClassRecord) => {
        const color =
          record.recordKind === ClassRecordKind.MAKEUP_USAGE
            ? 'green'
            : value === ClassRecordStatus.ATTENDED
              ? 'blue'
              : value === ClassRecordStatus.PENDING
                ? 'default'
                : value === ClassRecordStatus.LEAVE_MAKEUP
                  ? 'orange'
                  : value === ClassRecordStatus.LEAVE_CONVERTED_TO_MONEY
                    ? 'gold'
                    : 'purple'
        return <Tag color={color}>{CLASS_RECORD_STATUS_LABEL[value]}</Tag>
      },
    },
    {
      title: '配置',
      key: 'dailySequence',
      width: 300,
      render: (_: unknown, record: ClassRecord) => {
        const sequence = dailySequenceMap[idKey(record.id)] ?? 1
        const dateKey = dayjs(record.classDate).format('YYYY-MM-DD')
        const fallbackMonthlyPrice =
          record.generationBatchId !== undefined && record.generationBatchId !== null
            ? generationBatchMonthlyPriceMap[String(record.generationBatchId)] ?? null
            : currentConfigMonthlyPrice ?? null
        const sessionLabel = `第${record.sessionIndex ?? sequence}节`
        const configText = getRecordConfigText(
          sessionLabel,
          record.classType,
          record.teacherName,
          record.billingMode,
          record.coursePrice,
          fallbackMonthlyPrice,
          unifiedMonthlyPriceByDate[dateKey],
        )

        return (
          <Tag
            color={getDailySequenceColor(sequence)}
            className="m-0 rounded-full px-2"
          >
            {configText}
          </Tag>
        )
      },
    },
    {
      title: '时长',
      dataIndex: 'durationMinutes',
      key: 'durationMinutes',
      width: 96,
      render: (value: number) => `${value} 分钟`,
    },
    {
      title: '备注',
      dataIndex: 'note',
      key: 'note',
      render: (_: string | null | undefined, record: ClassRecord) => {
        const key = idKey(record.id)
        const value = noteDraftMap[key] ?? record.note ?? ''
        return (
          <div className="rounded-md border border-transparent bg-white/80 px-1.5 py-0.5 transition-all hover:border-gray-200 focus-within:border-blue-300">
            <Input
              value={value}
              maxLength={200}
              size="small"
              variant="borderless"
              placeholder="添加备注"
              onChange={(event) => {
                const { value: nextValue } = event.target
                setNoteDraftMap((prev) => ({
                  ...prev,
                  [key]: nextValue,
                }))
              }}
              onBlur={() => onNoteBlur(record)}
              suffix={noteSavingMap[key] ? '保存中' : undefined}
            />
          </div>
        )
      },
    },
  ]
}
